import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_COLOUR = { W: "bg-emerald-500/20 text-emerald-300", D: "bg-slate-600/40 text-slate-300", L: "bg-red-500/20 text-red-300" };

/** Running record for each league side, built from the scores coaches enter. */
export const LeagueTable = ({ refreshKey }) => {
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/academy/league-table`, { withCredentials: true });
        setTeams(r.data.teams || []);
      } catch (_) { /* table is a read-only extra */ }
    })();
  }, [refreshKey]);

  if (!teams.length) return null;

  return (
    <Card className="bg-slate-900/60 border-slate-800 p-4 sm:p-6" data-testid="league-table-card">
      <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-amber-400" /> Season record
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Built from the scores entered after each game. Parents see their own team&apos;s record on
        their dashboard.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-800">
              <th className="py-2 pr-3">Team</th>
              <th className="py-2 px-2 text-center">P</th>
              <th className="py-2 px-2 text-center">W</th>
              <th className="py-2 px-2 text-center">D</th>
              <th className="py-2 px-2 text-center">L</th>
              <th className="py-2 px-2 text-center">GF</th>
              <th className="py-2 px-2 text-center">GA</th>
              <th className="py-2 px-2 text-center">GD</th>
              <th className="py-2 px-2 text-center">Pts</th>
              <th className="py-2 pl-2">Form</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.team_id} className="border-b border-slate-800/50"
                data-testid={`league-row-${t.team_id}`}>
                <td className="py-2 pr-3">
                  <p className="text-white">{t.team_name}</p>
                  {t.league_name && <p className="text-[11px] text-slate-500">{t.league_name}</p>}
                </td>
                <td className="py-2 px-2 text-center text-slate-300">{t.played}</td>
                <td className="py-2 px-2 text-center text-slate-300">{t.won}</td>
                <td className="py-2 px-2 text-center text-slate-300">{t.drawn}</td>
                <td className="py-2 px-2 text-center text-slate-300">{t.lost}</td>
                <td className="py-2 px-2 text-center text-slate-400">{t.goals_for}</td>
                <td className="py-2 px-2 text-center text-slate-400">{t.goals_against}</td>
                <td className="py-2 px-2 text-center text-slate-400">
                  {t.goal_difference > 0 ? `+${t.goal_difference}` : t.goal_difference}
                </td>
                <td className="py-2 px-2 text-center font-mono text-white"
                  data-testid={`league-points-${t.team_id}`}>{t.points}</td>
                <td className="py-2 pl-2">
                  <div className="flex gap-1">
                    {(t.form || []).map((f, i) => (
                      <span key={i}
                        className={`w-5 h-5 rounded text-[10px] grid place-items-center ${FORM_COLOUR[f]}`}>
                        {f}
                      </span>
                    ))}
                    {(t.form || []).length === 0 && <span className="text-[11px] text-slate-600">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 space-y-1">
        {teams.flatMap((t) => (t.results || []).slice(0, 3).map((f) => (
          <p key={f.id} className="text-[11px] text-slate-400" data-testid={`result-line-${f.id}`}>
            {f.date} · {t.team_name} {f.result.our_score}–{f.result.their_score} {f.opponent}
            <Badge className={`ml-2 text-[9px] ${FORM_COLOUR[
              f.result.outcome === "win" ? "W" : f.result.outcome === "draw" ? "D" : "L"]}`}>
              {f.result.outcome}
            </Badge>
          </p>
        )))}
      </div>
    </Card>
  );
};
